import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChatListRefetchOptions } from '@/hooks/useDirectChats';
import { supabase } from '@/lib/supabase';
import { cacheRows, cachedRows } from '@/utils/chatListCache';
import { isInDepartment } from '@/utils/departments';
import { isEventLive } from '@/utils/isEventLive';
import { logger } from '@/utils/logger';

/** Matches the bound applied to the other chat lists. */
/**
 * Per event, not global -- an event with more than this many departments would be
 * remarkable, where the old global cap was reachable by an org with a few dozen events.
 */
export const DEPARTMENT_CHATS_LIMIT = 200;

export type DepartmentChatRow = {
  id: string;
  eventId: string;
  eventName: string | null;
  eventImage: string | null;
  organizationId: string;
  department: string;
  createdAt: string;
  lastMessageAt: string | null;
};

type RawChat = {
  id: string;
  event_id: string;
  organization_id: string;
  department: string;
  created_at: string;
  last_message_at: string | null;
};

type RawEvent = {
  id: string;
  name: string | null;
  image: string | null;
  end_date: string | null;
  is_active: boolean | null;
  departments: string[] | null;
};

/**
 * Department chats visible to this user.
 *
 * RLS decides what comes back — "department_chats: members read" admits the volunteers
 * assigned to that department on that event plus any admin of the owning org — so this
 * deliberately does not filter by viewer id.
 *
 * Two client-side filters on top of that, both matching what the other chat lists do:
 * ended events drop out, and so do chats whose department has since been removed from
 * events.departments. The row itself is never deleted, so re-adding the department brings
 * the thread back with its history — see 20260903120000_department_chats.sql.
 */
export function useDepartmentChats(
  userId: string | null,
  authLoading = false,
  /**
   * The event to load, or null for none.
   *
   * Required rather than optional. An org account is a member of every department of every
   * event it owns, so an unscoped read returns events x departments rows; fetching the
   * newest 200 globally and filtering afterwards meant a quiet event's chats could fall
   * outside that window and silently never render. Making it required also avoids one
   * unscoped query on first paint, before the event selection has settled.
   */
  eventId: string | null
) {
  // Per viewer AND per event, since this list is scoped to the selected event.
  const cacheKey = userId && eventId ? `department:${userId}:${eventId}` : null;
  // Seeded rather than filled by an effect: an effect runs after the first paint, which
  // is the blank list we are trying not to show. See utils/chatListCache.
  const [chats, setChats] = useState<DepartmentChatRow[]>(
    () => cachedRows<DepartmentChatRow>(cacheKey) ?? []
  );
  const [loading, setLoading] = useState(() => cachedRows(cacheKey) == null);
  const [error, setError] = useState<string | null>(null);
  /** Which viewer/event pair is in state, so only a change of those clears the rows. */
  const cacheKeyRef = useRef<string | null>(cacheKey);

  const fetchChats = useCallback(async (options?: ChatListRefetchOptions) => {
    const quiet = options?.quiet === true;
    // Rows for this event already in hand: show them and refresh underneath. Switching
    // events misses, which drops the previous event's rows.
    const cached = cachedRows<DepartmentChatRow>(cacheKey);
    if (cacheKeyRef.current !== cacheKey) {
      cacheKeyRef.current = cacheKey;
      setChats(cached ?? []);
    } else if (cached) {
      setChats(cached);
    }
    if (authLoading) {
      if (!cached) setLoading(true);
      return;
    }
    if (!userId || !eventId) {
      setChats([]);
      setLoading(false);
      return;
    }

    if (cached) setLoading(false);
    else if (!quiet) setLoading(true);
    setError(null);
    try {
      const { data: chatRows, error: chatError } = await supabase
        .from('department_chats')
        .select('id, event_id, organization_id, department, created_at, last_message_at')
        .eq('event_id', eventId)
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .limit(DEPARTMENT_CHATS_LIMIT);

      if (chatError) throw chatError;

      const raw = (chatRows ?? []) as RawChat[];
      if (raw.length === 0) {
        const empty: DepartmentChatRow[] = [];
        cacheRows(cacheKey, empty);
        setChats(empty);
        return;
      }
      if (raw.length === DEPARTMENT_CHATS_LIMIT) {
        logger.warn('useDepartmentChats: hit the row cap for this event', {
          limit: DEPARTMENT_CHATS_LIMIT,
          eventId,
        });
      }

      const eventIds = [...new Set(raw.map((r) => r.event_id))];
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('id, name, image, end_date, is_active, departments')
        .in('id', eventIds);

      if (eventError) throw eventError;

      const eventsById = new Map<string, RawEvent>(
        ((eventData ?? []) as RawEvent[]).map((e) => [e.id, e])
      );

      const now = Date.now();
      const rows = raw
        .filter((r) => {
          const event = eventsById.get(r.event_id);
          if (!isEventLive(event, now)) return false;
          // The department was dropped from the event: the thread stays in the database
          // but stops being listed. Compared with the same case/whitespace rule the RLS
          // predicate uses, via the shared helper.
          return (event?.departments ?? []).some((d) => isInDepartment(d, r.department));
        })
        .map((r) => ({
          id: r.id,
          eventId: r.event_id,
          eventName: eventsById.get(r.event_id)?.name ?? null,
          eventImage: eventsById.get(r.event_id)?.image ?? null,
          organizationId: r.organization_id,
          department: r.department,
          createdAt: r.created_at,
          lastMessageAt: r.last_message_at,
        }));

      // Only a successful read is remembered, so an error leaves the last good rows for
      // the next mount rather than caching a blank list.
      cacheRows(cacheKey, rows);
      setChats(rows);
    } catch (e) {
      logger.error('useDepartmentChats', e);
      setError(e instanceof Error ? e.message : 'Failed to load department chats');
      setChats([]);
    } finally {
      setLoading(false);
    }
  }, [userId, authLoading, eventId, cacheKey]);

  useEffect(() => {
    void fetchChats();
  }, [fetchChats]);

  return { chats, loading, error, refetch: fetchChats };
}
